/**
 * Sync engine ของเครื่องหน้างาน — ระบายคิวออฟไลน์ขึ้น server (D-078)
 *
 * นี่คือครึ่งหลังของ offline-first loop: หน้าจอสแกน (D-075) เขียนคำสั่งลงคิว
 * ไฟล์นี้เป็นตัวที่เอาขึ้น `POST /v1/offline-sync/commands` แล้วคัดคำตอบรายคำสั่ง
 * กลับลงคิว
 *
 * ลำดับหนึ่งรอบ (`drainOfflineCommands`):
 *   1. อ่านคำสั่งที่ยังต้องส่ง (`PENDING_LOCAL` + `SYNCING` ที่ค้างจากรอบก่อน)
 *      เรียงตาม `device_sequence_no`
 *   2. mark ทั้งชุดเป็น `SYNCING` **ก่อน** ยิง request
 *   3. POST หนึ่ง batch `{ device_id, commands }`
 *   4. ตอบ `200` → คัดผลรายคำสั่ง (`ACCEPTED`/`REJECTED`/`CONFLICT`) ลงแถวตาม
 *      `client_event_id`; ล้มเหลวแบบอื่น → คืนทั้งชุดกลับเป็น `PENDING_LOCAL`
 *
 * ทำไมการส่งซ้ำถึงปลอดภัย: `client_event_id` เป็น idempotency key ที่ server
 * dedupe ทั้งชั้น sync และชั้น `request_dedup` (D-076) คำสั่งที่ค้าง `SYNCING`
 * เพราะ response หายกลางทางจึงถูกส่งใหม่แล้ว **replay คำตอบเดิม** ไม่ใช่ post ซ้ำ
 * ผลลัพธ์ที่ยอมไม่ได้คือคำสั่งที่หายไปเงียบ ๆ ไม่ใช่คำสั่งที่ถูกส่งสองครั้ง
 *
 * สิ่งที่ไฟล์นี้ **ไม่ทำ**:
 *   - ไม่แก้และไม่ลบ `CONFLICT`/`REJECTED` — แค่ mark ไว้ให้เห็น การตัดสินเป็นของ
 *     คนผ่านหน้าจอ conflict (WO แยกที่กิน `GET /v1/offline-sync/conflicts`) และ
 *     ห้าม force post เด็ดขาด (business rule 5)
 *   - ไม่มี background interval และไม่มี Service Worker background sync — D-078
 *     ให้ Phase 1 มีแค่ปุ่ม "ซิงค์ตอนนี้" กับตอน reconnect
 *   - ไม่ตีความ payload และไม่คิดผลของคำสั่งเอง — server เป็นผู้ตัดสินเสมอ
 */

import {
  ACCEPTED,
  CONFLICT,
  REJECTED,
  type OfflineCommandRecord,
  type ServerSyncStatus,
} from './commandEnvelope';
import {
  applyServerSyncStatuses,
  markCommandsSyncing,
  readDrainableCommands,
  revertSyncingToPending,
} from './commandQueue';
import {
  buildSyncCommandsBody,
  postOfflineSyncCommands,
  type SyncRequestOptions,
} from './syncClient';
import { readShopFloorIdentity, type ShopFloorIdentity } from '../shopFloor/identity';

/** เหตุที่รอบนี้จบโดยไม่มีคำตอบ — ทุกกรณีคำสั่งยังอยู่ในคิว ไม่มีใบไหนหายไป */
export type DrainFailureReason =
  /** คิวในเครื่องอ่าน/เขียนไม่ได้ (พื้นที่เต็ม, storage ถูกบล็อก) */
  | 'QUEUE_UNAVAILABLE'
  /** session หมดอายุหรือถูกเพิกถอน — ต้องเข้าสู่ระบบใหม่ก่อนถึงจะซิงค์ได้ */
  | 'UNAUTHENTICATED'
  /** server ปฏิเสธทั้ง batch (`4xx`) เช่นอุปกรณ์ยังไม่ได้ลงทะเบียน */
  | 'BATCH_REJECTED'
  | 'SERVER_ERROR'
  | 'NETWORK'
  /** ตอบ `200` แต่รูปร่างไม่ตรง contract — ถือว่ายังไม่รู้ผล */
  | 'INVALID_RESPONSE';

export type DrainResult =
  /** ไม่ได้ส่งอะไรเลยในรอบนี้ และไม่ใช่ความล้มเหลว */
  | { readonly status: 'skipped'; readonly reason: 'ALREADY_RUNNING' | 'NOTHING_TO_SYNC' }
  | {
      readonly status: 'synced';
      readonly sent: number;
      readonly accepted: number;
      readonly conflicted: number;
      readonly rejected: number;
      /** ส่งไปแล้วแต่ไม่มีผลกลับมาให้ — คืนเข้าคิวรอรอบหน้า */
      readonly unanswered: number;
    }
  | {
      readonly status: 'failed';
      readonly reason: DrainFailureReason;
      /** จำนวนที่ถูกคืนกลับเป็น `PENDING_LOCAL` */
      readonly returned: number;
      /** rule token ของการปฏิเสธ ถ้า server บอกมา */
      readonly rule: string | null;
    };

export interface DrainOptions extends SyncRequestOptions {
  /** inject ได้เพื่อการทดสอบ — production อ่านจาก seam เดียวใน `lib/shopFloor` */
  readonly identity?: ShopFloorIdentity;
}

/**
 * drain ที่กำลังทำงานอยู่ — one drain at a time (D-078)
 *
 * ถือเป็น flag ระดับโมดูล ไม่ใช่ state ของหน้าจอ เพราะคิวมีชุดเดียวต่อเครื่อง:
 * ปุ่ม "ซิงค์ตอนนี้" ที่ถูกกดตอน reconnect กำลังระบายอยู่ ต้องไม่ทำให้คำสั่งชุด
 * เดียวกันถูกหยิบไปสองที่แล้วสถานะทับกันเอง
 */
let drainInFlight = false;

/** มี drain ค้างอยู่หรือไม่ — หน้าจอใช้บอกผู้ใช้ ไม่ใช่ใช้ตัดสินใจแทน engine */
export function isDrainInFlight(): boolean {
  return drainInFlight;
}

function tally(
  records: readonly OfflineCommandRecord[],
  statuses: ReadonlyMap<string, ServerSyncStatus>,
  wanted: ServerSyncStatus,
): number {
  return records.filter((record) => statuses.get(record.client_event_id) === wanted).length;
}

/**
 * ระบายคิวหนึ่งรอบ
 *
 * คืนผลลัพธ์แทนการโยนเสมอ: การซิงค์ที่ล้มเหลวเป็นเรื่องปกติของเครื่องหน้างาน
 * (WAN หลุดกลางโรงงาน) หน้าจอต้องเล่าให้ผู้ปฏิบัติงานฟังได้ ไม่ใช่กลายเป็น
 * unhandled rejection แล้วปุ่มค้างอยู่เฉย ๆ
 */
export async function drainOfflineCommands(options: DrainOptions = {}): Promise<DrainResult> {
  if (drainInFlight) {
    return { status: 'skipped', reason: 'ALREADY_RUNNING' };
  }
  drainInFlight = true;

  try {
    let records: OfflineCommandRecord[];
    try {
      records = await readDrainableCommands();
    } catch {
      return { status: 'failed', reason: 'QUEUE_UNAVAILABLE', returned: 0, rule: null };
    }

    if (records.length === 0) {
      return { status: 'skipped', reason: 'NOTHING_TO_SYNC' };
    }

    const clientEventIds = records.map((record) => record.client_event_id);
    const identity = options.identity ?? readShopFloorIdentity();

    try {
      await markCommandsSyncing(clientEventIds);
    } catch {
      /* เขียนสถานะไม่ได้ ก็ยังไม่ส่ง — คำสั่งที่ถูกส่งโดยคิวไม่รู้ตัวคือคำสั่งที่จะถูกส่งซ้ำ */
      return { status: 'failed', reason: 'QUEUE_UNAVAILABLE', returned: 0, rule: null };
    }

    const body = await buildSyncCommandsBody(identity.device_id, records);
    const response = await postOfflineSyncCommands(body, options);

    if (response.status !== 'results') {
      const returned = await returnToQueue(clientEventIds);
      return {
        status: 'failed',
        reason: failureReasonOf(response.status),
        returned,
        rule: response.status === 'rejected' ? response.rule : null,
      };
    }

    /* รับเฉพาะผลของคำสั่งที่อยู่ใน batch นี้ — คิวนี้ไม่ใช่ที่เก็บผลของเครื่องอื่น */
    const sentIds = new Set(clientEventIds);
    const statuses = new Map<string, ServerSyncStatus>();
    for (const result of response.results) {
      if (sentIds.has(result.client_event_id)) {
        statuses.set(result.client_event_id, result.sync_status);
      }
    }

    try {
      await applyServerSyncStatuses(statuses);
    } catch {
      /*
       * server ตัดสินแล้วแต่เครื่องจดไม่ลง — ปล่อยค้าง `SYNCING` ไว้อย่างนั้น
       * รอบหน้าจะส่งใหม่แล้วได้คำตอบเดิมกลับมา (replay) ซึ่งถูกต้องกว่าการเดา
       */
      return { status: 'failed', reason: 'QUEUE_UNAVAILABLE', returned: 0, rule: null };
    }

    /* คำสั่งที่ส่งไปแล้วไม่มีคำตอบ ต้องกลับเข้าคิว ไม่ใช่ค้างเป็น SYNCING ตลอดกาล */
    const unansweredIds = clientEventIds.filter((id) => !statuses.has(id));
    const unanswered = await returnToQueue(unansweredIds);

    return {
      status: 'synced',
      sent: records.length,
      accepted: tally(records, statuses, ACCEPTED),
      conflicted: tally(records, statuses, CONFLICT),
      rejected: tally(records, statuses, REJECTED),
      unanswered,
    };
  } finally {
    drainInFlight = false;
  }
}

function failureReasonOf(status: 'unauthenticated' | 'rejected' | 'server-error' | 'network-error' | 'invalid-response'): DrainFailureReason {
  switch (status) {
    case 'unauthenticated':
      return 'UNAUTHENTICATED';
    case 'rejected':
      return 'BATCH_REJECTED';
    case 'server-error':
      return 'SERVER_ERROR';
    case 'network-error':
      return 'NETWORK';
    default:
      return 'INVALID_RESPONSE';
  }
}

/**
 * คืนคำสั่งกลับเป็น `PENDING_LOCAL`
 *
 * การคืนที่ล้มเหลวไม่ทำให้รอบนี้กลายเป็นอย่างอื่น: แถวที่ค้าง `SYNCING` ยังถูก
 * หยิบไปในรอบถัดไปเสมอ (`readDrainableCommands`) จึงไม่มีคำสั่งใบไหนหลุดออกจากคิว
 */
async function returnToQueue(clientEventIds: readonly string[]): Promise<number> {
  if (clientEventIds.length === 0) {
    return 0;
  }
  try {
    return await revertSyncingToPending(clientEventIds);
  } catch {
    return 0;
  }
}
